import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import { Claim } from '@wsphere/warranties/domain';
import { ClaimService } from '../../services';

const { Action, State } = Claim.Override;

const STATE_BADGES: Record<Claim.Override.State, { text: string; theme: ThemeColor }> = {
  [State.PENDING]: { text: 'Waiting', theme: 'warn' },
  [State.APPROVED]: { text: 'Approved', theme: 'success' },
  [State.REJECTED]: { text: 'Rejected', theme: 'danger' },
  [State.REVERSED]: { text: 'Reversed', theme: 'default' },
};

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-overrides',
  templateUrl: './claim-overrides.component.html',
  standalone: false,
})
export class ClaimOverridesComponent {
  constructor(
    private claimSvc: ClaimService,
    private toastSvc: ToastService
  ) {}

  @ViewChild(DialogComponent) private dialog?: DialogComponent;

  @Input() claim: Claim.Model | null = null;

  /** Loaded by the page with the claim. */
  @Input() overrides: Claim.Override.Model[] = [];

  /** Authorizations and shares with an issued or completed payment; their approvals can't be reversed. */
  @Input() paidTargetIds: string[] = [];

  /** Staff with clm_ManageOverrides for this claim's distributor. */
  @Input() canManage = false;

  /** Emits after a decision, so the page reloads. */
  @Output() changed = new EventEmitter<void>();

  protected readonly Action = Action;
  protected readonly State = State;
  protected readonly nextStep = Claim.Override.nextStep;

  /** The override and action waiting for a reason. */
  protected deciding: { override: Claim.Override.Model; action: Claim.Override.Action } | null = null;
  protected reason = new FormControl('', { nonNullable: true, validators: [Validators.required] });
  protected saving = false;

  describe(override: Claim.Override.Model) {
    const amount = new Intl.NumberFormat('en-US', { style: 'currency', currency: override.currency }).format(
      override.requestedAmount / 100
    );
    const rule = Claim.Override.describe(override);
    return `${rule.charAt(0).toUpperCase()}${rule.slice(1)} · ${amount}`;
  }

  badge(override: Claim.Override.Model) {
    return STATE_BADGES[override.state];
  }

  /** Approving needs no reason; rejecting and reversing ask for one first. */
  async decide(override: Claim.Override.Model, action: Claim.Override.Action) {
    if (action === Action.APPROVE) {
      await this.send(override, action, null);
      return;
    }

    this.deciding = { override, action };
    this.reason.reset();
    this.dialog?.open();
  }

  async confirm() {
    if (!this.deciding || this.reason.invalid) return;
    await this.send(this.deciding.override, this.deciding.action, this.reason.value.trim());
  }

  close() {
    this.dialog?.close();
  }

  private async send(override: Claim.Override.Model, action: Claim.Override.Action, reason: string | null) {
    if (!this.claim) return;
    this.saving = true;

    const decided = await this.claimSvc
      .decideOverride(this.claim.id, override.id, { action, reason })
      .catch(({ error }) => {
        this.toastSvc.show({ type: 'error', title: 'Something went wrong', text: error.message });
        return null;
      });

    this.saving = false;
    if (!decided) return;

    this.close();
    this.changed.emit();
  }
}
